import { getTypeByIndex as getOverviewTypeByIndex } from '../../../../common/dashboards/enums/OverviewType';
import { TrendData } from '../../../../common/dashboards/models/TrendData';
import { DateUtils } from '../../../../common/utils/DateUtils';
import { GQLIdEncoder } from '../../../../common/utils/GQLIdEncoder';
import { CsvInventoryMapper } from '../../common/mappers/CsvInventoryMapper';
import { CsvPayload } from '../../common/models/CsvPayload';
import { OrganizationCsvData } from '../models/OrganizationCsvData';

export class OrganizationCsvMapper {
  private readonly inventoryMapper = new CsvInventoryMapper();
  private readonly gqlIdEncoder = new GQLIdEncoder();

  mapGQLModel(data: any, selectedOverviews: string[], trendsData: TrendData[] = []): OrganizationCsvData {
    const overview = this.mapOverview(data.overview, selectedOverviews);
    const inventory = this.inventoryMapper.mapGQLModel(data.inventory);
    const issues = this.mapIssues(data.vulnerabilities);
    const trends = this.mapTrends(trendsData);

    return new OrganizationCsvData(overview, trends, inventory, issues);
  }

  private mapOverview(data: any, selectedOverviews: string[]): CsvPayload {
    const overviews = data?.overviews ?? [];
    const headers = ['Trace type', 'High instances', 'Medium instances', 'Low instances', 'Passed instances'];
    const content: string[][] = [];
    overviews.forEach((os: any, index: number) => {
      const id = getOverviewTypeByIndex(index);
      if (!os || !selectedOverviews.includes(id)) {
        return;
      }
      const { title, overview: o } = os;
      content.push([title, o.highInstances.count, o.mediumInstances.count, o.lowInstances.count, o.passedInstances.count]);
    });

    return new CsvPayload(headers, content);
  }

  private mapTrends(trendsData: TrendData[]): CsvPayload {
    const headers = ['From', 'To', 'High instances', 'Medium instances', 'Low instances', 'Passed instances'];
    const content: string[][] = [];
    for (const trend of trendsData) {
      content.push([
        DateUtils.toISOString(trend.getFrom()),
        DateUtils.toISOString(trend.getTo()),
        trend.getHighInstances().toString(),
        trend.getMediumInstances().toString(),
        trend.getLowInstances().toString(),
        trend.getPassedInstances().toString(),
      ]);
    }

    return new CsvPayload(headers, content);
  }

  private mapIssues(data: any): CsvPayload {
    const issueSummaryHeaders = ['Project ID', 'Project name', 'Number', 'Title', 'Explanation', 'Severity', 'Reports impacted'];
    const issues = (data?.edges ?? []).map(({ node }: any) => {
      const { number, title, shortExplanation, severity, numberOfSlots, project } = node ?? {};
      const projectData = project
        ? { id: this.gqlIdEncoder.decode(project.id), name: project.name }
        : {
            id: '',
            name: '',
          };
      return [projectData.id, projectData.name, number, title, shortExplanation, severity, numberOfSlots];
    });

    return new CsvPayload(issueSummaryHeaders, issues);
  }
}
